import { db } from './firebase/config';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  getDoc,
  serverTimestamp
} from 'firebase/firestore';

const modelsCollection = collection(db, 'models');

/**
 * Ecoute en temps réel tous les modèles avec gestion des anciens modèles sans createdBy
 * @param {Function} onUpdate - Callback appelé avec la liste des modèles
 * @returns {Function} Fonction de désabonnement
 */
const getAllModels = (onUpdate) => {
  return onSnapshot(modelsCollection, (snapshot) => {
    const models = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Compatibilité avec les anciens modèles
        createdBy: data.createdBy || 'Admin',
        createdAt: data.createdAt || null,
        updatedAt: data.updatedAt || null
      };
    });
    onUpdate(models);
  }, (error) => {

    onUpdate([]);
  });
};

/**
 * Ecoute en temps réel les modèles d'un utilisateur avec filtrage côté client
 * @param {string} userId - ID de l'utilisateur
 * @param {Function} onUpdate - Callback appelé avec la liste des modèles filtrés
 * @returns {Function} Fonction de désabonnement
 */
const getUserModels = (userId, onUpdate) => {
  return onSnapshot(modelsCollection, (snapshot) => {
    const allModels = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Compatibilité avec les anciens modèles
        createdBy: data.createdBy || 'Admin',
        createdAt: data.createdAt || null,
        updatedAt: data.updatedAt || null
      };
    });
    
    // Filtrage côté client pour les modèles de l'utilisateur
    const userModels = allModels.filter(model => model.createdBy === userId);
    onUpdate(userModels);
  }, (error) => {

    onUpdate([]);
  });
};

/**
 * Fonction de compatibilité - utilise getAllModels
 * @param {Function} callback - Callback appelé avec la liste des modèles
 * @returns {Function} Fonction de désabonnement
 */
const subscribeToModels = (callback) => {
  return getAllModels(callback);
};

/**
 * Créer un modèle avec timestamps, createdBy et propriétés étendues
 * @param {Object} data - Données du modèle
 * @param {string} userId - ID de l'utilisateur créateur
 * @returns {Promise<string>} ID du modèle créé
 */
const createModel = async (data, userId) => {
  const modelData = {
    ...data,
    createdBy: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  
  const docRef = await addDoc(modelsCollection, modelData);
  return docRef.id;
};

/**
 * Mettre à jour un modèle avec timestamp updatedAt
 * @param {string} id - ID du modèle
 * @param {Object} data - Données à mettre à jour
 */
const updateModel = async (id, data) => {
  const ref = doc(db, 'models', id);
  const updateData = {
    ...data,
    updatedAt: serverTimestamp()
  };
  await updateDoc(ref, updateData);
};

const deleteModel = async (id) => {
  const ref = doc(db, 'models', id);
  await deleteDoc(ref);
};

/**
 * Récupérer un modèle avec propriétés par défaut pour compatibilité
 * @param {string} id - ID du modèle
 * @returns {Promise<Object|null>} Modèle avec propriétés par défaut
 */
const getModel = async (id) => {
  const ref = doc(db, 'models', id);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    const data = snap.data();
    return {
      id: snap.id,
      ...data,
      // Compatibilité avec les anciens modèles
      createdBy: data.createdBy || 'Admin',
      createdAt: data.createdAt || null,
      updatedAt: data.updatedAt || null
    };
  }
  return null;
};

export default {
  getAllModels,
  getUserModels,
  subscribeToModels,
  createModel,
  updateModel,
  deleteModel,
  getModel
}; 